import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Calendar, LogOut, Menu, Plus, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { SYSTEM_ROLES } from '@lms/shared';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { useViewModeStore } from '@/stores/viewMode';
import { Button } from '@/components/ui/button';
import { BackToTop } from '@/components/BackToTop';
import { cn, fullName } from '@/lib/utils';
import { UserAvatar } from '@/components/UserAvatar';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { NotificationBell } from './NotificationBell';
import { ViewSwitch } from './ViewSwitch';
import { NAV_SECTIONS, hasTwoViews, visibleSections, type NavCounts } from './navigation';
import { isSittingAssessment, railState } from './sidebarRail';

export function AppShell() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const viewMode = useViewModeStore((s) => s.mode);
  const location = useLocation();
  const navigate = useNavigate();

  const [mobileOpen, setMobileOpen] = useState(false);

  /**
   * One request for all three badges, held for a few minutes.
   *
   * The shell renders on every page, so anything fetched here is paid for on
   * every navigation. These change when someone finishes a course or earns a
   * certificate — minutes-fresh is fresh enough.
   */
  const { data: counts } = useQuery({
    queryKey: ['nav-counts'],
    queryFn: async () => {
      const { data } = await api.get<NavCounts>('/dashboard/nav-counts');
      return data;
    },
    staleTime: 3 * 60 * 1000,
    enabled: Boolean(user),
  });

  /**
   * ⌘K / Ctrl+K opens the catalogue.
   *
   * Declared before the early return so the hook order never changes between
   * renders. `preventDefault` stops the browser's own Ctrl+K, which focuses
   * the address bar and would otherwise steal the shortcut.
   */
  const inAttempt = isSittingAssessment(location.pathname);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        // Still swallowed during an attempt — the browser's own Ctrl+K would
        // otherwise take them to the address bar, which is its own way out.
        if (inAttempt) return;
        navigate('/catalogue');
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navigate, inAttempt]);

  /**
   * The Back button, while a paper is open.
   *
   * Pushing a duplicate entry and re-pushing it on every `popstate` means Back
   * lands where it started. Crude, and it is the only thing available: a page
   * cannot veto navigation, only replace what it navigates to.
   *
   * Not a jail. The clock runs to zero and submits regardless, so this delays
   * an exit by as long as the paper has left — it does not create a state
   * nobody can leave. Reloading, closing the tab and signing out elsewhere all
   * still work, and all end the same way.
   */
  useEffect(() => {
    if (!inAttempt) return;

    window.history.pushState(null, '', window.location.href);
    const onPopState = () => {
      window.history.pushState(null, '', window.location.href);
      toast.warning('Finish or submit the assessment before leaving.');
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [inAttempt]);

  if (!user) return null;

  const roleSlugs = user.roles.map((role) => role.slug);
  /**
   * Two jobs, so two views.
   *
   * An Admin has always had this. A Trainer has it for the same reason: they
   * build the organisation's training AND sit their own, and those are
   * different menus over the same account. A Manager does not — their menu is
   * one thing with a department attached.
   */
  const canSwitchView = hasTwoViews(roleSlugs);
  /**
   * Writing an announcement is the one create action worth a place up here.
   *
   * Everything else somebody creates — a course, an assessment, a user — is
   * made while already on the screen that lists it. An announcement is the
   * opposite: the reason to write one arrives while you are somewhere else
   * entirely, and the old path was to remember the page existed, navigate to
   * it, and then press the button waiting there.
   */
  const canCompose = user.permissions.includes('announcement.create');
  const sections = visibleSections(user.permissions, roleSlugs, viewMode);

  /** Admin / Manager / Employee — the first system role the person holds. */
  /**
   * The label under the name.
   *
   * Read from the ROLE rather than from scope, now that two roles are
   * organisation-wide: scope alone would call a Trainer an Admin, which is the
   * one thing this label exists to distinguish.
   */
  const primaryRole = roleSlugs.includes(SYSTEM_ROLES.ADMIN)
    ? 'Admin'
    : roleSlugs.includes(SYSTEM_ROLES.TRAINER)
      ? 'Trainer'
      : user.permissions.includes('scope.department')
        ? 'Manager'
        : 'Employee';

  /**
   * Sitting an exam collapses the sidebar to its icons.
   *
   * Two reasons, and the second is the one that matters. The paper deserves
   * the width — a question with five options and a screenshot is cramped
   * against a 236px menu. And every one of those links leads OUT of a timed
   * attempt whose clock does not stop, so making them a deliberate act rather
   * than a stray click is a kindness.
   *
   * Collapsed, not hidden: somebody who genuinely means to leave still can,
   * and a menu that vanishes reads as a bug. The route is the trigger, so it
   * needs no state and cannot get stuck collapsed after the attempt ends.
   */
  const sittingAssessment = inAttempt;

  /**
   * ── THE RAIL ──────────────────────────────────────────────────────────────
   *
   * A course player is three columns of its own — contents, viewer, notes —
   * and 236px of menu beside them is width the video wanted. So the sidebar
   * narrows to a rail of icons when somebody opens a course.
   *
   * Unlike an exam, this is a COURTESY rather than a control: every link stays
   * live and clickable, and nothing is lost but the words beside the icons.
   *
   * Decided in `sidebarRail.ts`, including why the brand mark is a link home
   * everywhere except mid-paper.
   */
  const { railed, brandLinksHome } = railState(location.pathname);

  /**
   * Takes `collapsed` rather than reading it, because the two places this is
   * rendered want different answers: the desktop rail collapses during an
   * attempt, the mobile drawer never does — it is already a deliberate act to
   * open it, and an icon-only drawer would be a worse menu for no gain.
   */
  const navList = (collapsed: boolean, locked = false) => (
    /**
     * `min-h-0` with `overflow-y-auto` is what makes this column not scroll.
     *
     * A flex child will not shrink below its content without `min-h-0`, so the
     * old nav pushed the sidebar taller than the screen and the *page* scrolled
     * — which is the scrollbar that was actually showing. With the rhythm
     * below, an employee and a manager nav both fit outright and nothing
     * scrolls; only a full admin menu on a short window can still reach for it,
     * and hiding the overflow there would put Settings out of reach entirely.
     */
    // `overscroll-y-contain` stops the end of the menu handing the wheel to
    // the page behind, which is the hand-off that read as lag. On the Y axis
    // only, and stated here rather than inside `scrollbar-thin` — that class
    // also dresses horizontal strips, where containing Y stops the page
    // scrolling altogether.
    <nav className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-y-contain px-3 py-3 scrollbar-thin">
      {sections.map((section) => (
        <div key={section.title}>
          {/* Section headings are words; collapsed there is no room for them,
              and the divider below does the same job. */}
          {collapsed ? (
            <div className="mx-auto mb-1.5 h-px w-6 bg-border" aria-hidden />
          ) : (
            <p className="px-3 pb-1.5 text-2xs font-semibold uppercase text-muted-foreground/70">
              {section.title}
            </p>
          )}
          <ul className="space-y-0.5">
            {section.items.map((item) => {
              const Icon = item.icon;
              const badge = item.count ? counts?.[item.count] : undefined;

              return (
                <li key={item.to}>
                  {item.comingSoon ? (
                    <span
                      className={cn(
                        'flex cursor-not-allowed items-center gap-3 rounded-xl py-2 text-[13px] text-muted-foreground/50',
                        collapsed ? 'justify-center px-2' : 'px-3',
                      )}
                      title="Coming in a later phase"
                    >
                      <Icon className="h-[17px] w-[17px] shrink-0" strokeWidth={1.75} />
                      {!collapsed && <span className="truncate">{item.label}</span>}
                    </span>
                  ) : (
                    <NavLink
                      to={item.to}
                      end={item.to === '/'}
                      onClick={(e) => {
                        /**
                         * Sitting an exam, the menu is scenery.
                         *
                         * The clock does not stop for anything, so every link
                         * here is a way to lose time on a paper that will be
                         * submitted with or without the candidate. Left
                         * visible — a menu that vanishes reads as a fault —
                         * but inert until the paper is in.
                         */
                        if (locked) {
                          e.preventDefault();
                          return;
                        }
                        setMobileOpen(false);
                      }}
                      title={
                        locked
                          ? 'Finish the assessment first'
                          : collapsed
                            ? item.label
                            : undefined
                      }
                      /**
                       * Collapsed, the label is gone from the DOM and the link
                       * is an icon — which has no accessible name at all. A
                       * `title` is a weak fallback that some screen readers
                       * skip, so the name is stated outright.
                       */
                      aria-label={collapsed ? item.label : undefined}
                      aria-disabled={locked || undefined}
                      className={({ isActive }) =>
                        cn(
                          'relative flex items-center gap-3 rounded-xl py-2 text-[13px] transition-colors',
                          collapsed ? 'justify-center px-2' : 'px-3',
                          locked
                            ? 'cursor-not-allowed text-muted-foreground/40'
                            : isActive
                              ? 'bg-brand-800 font-medium text-white shadow-sm'
                              : 'text-foreground/70 hover:bg-muted hover:text-foreground',
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <Icon className="h-[17px] w-[17px] shrink-0" strokeWidth={1.75} />
                          {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
                          {/* Only when there is something to say — a grey zero
                              is noise on every link that has none. Collapsed
                              there is no room for a number, so it becomes a
                              dot: still "something here", without the digit. */}
                          {badge != null && badge > 0 && !collapsed && (
                            <span
                              className={cn(
                                'shrink-0 font-mono text-[11px]',
                                isActive ? 'text-white/70' : 'text-muted-foreground',
                              )}
                            >
                              {badge}
                            </span>
                          )}
                          {badge != null && badge > 0 && collapsed && (
                            <span
                              className={cn(
                                'absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full',
                                isActive ? 'bg-white/80' : 'bg-brand-700',
                              )}
                              aria-hidden
                            />
                          )}
                        </>
                      )}
                    </NavLink>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );

  /**
   * ── THE THREE HEADERS ARE ONE HEIGHT ─────────────────────────────────────
   *
   * The sidebar's brand block, the collapsed rail's mark, and the top bar all
   * sit along the top of the page with a border under them, so their borders
   * read as ONE line across the screen. They have to be the same height or
   * that line has a step in it, right at the corner where the eye starts.
   *
   * They had drifted to three different values — 61px, 57px and 56px — because
   * each was written where it was needed: the brand block had no height at all
   * and was whatever `py-3.5` around a 32px mark came to, the rail had a
   * hand-counted 57, and the header had 56. Nothing connected them, so nothing
   * noticed.
   *
   * `h-14` is 56px, and Tailwind's border-box means that INCLUDES the border,
   * so all three borders land on the same pixel. A stated height also stops
   * the brand block resizing itself if the wordmark's type ever changes.
   */
  const HEADER_HEIGHT = 'h-14';

  /** Boxed on white so the red mark keeps its contrast against a near-black
      sidebar in dark mode. */
  const brandMark = (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white ring-1 ring-border">
      <img src="/petpooja-mark.png" alt="" className="h-5 w-5 object-contain" />
    </span>
  );

  const brandWords = (
    <div className="min-w-0 leading-tight">
      <p className="truncate text-[13px] font-semibold">Petpooja</p>
      <p className="truncate text-[10px] text-muted-foreground">Learning Management System</p>
    </div>
  );

  /**
   * The logo is the way back to the dashboard.
   *
   * The one navigation people try without being told, from anywhere in the
   * product — so it is a real link rather than decoration.
   *
   * Not while a paper is open: every other link is deliberately inert there,
   * and a logo that went home would be the one way out of the screen the whole
   * lock exists to hold somebody on. It stays a plain mark instead.
   */
  const brandBlock = brandLinksHome ? (
    <NavLink
      to="/"
      onClick={() => setMobileOpen(false)}
      aria-label="Petpooja Learning Management System — go to the dashboard"
      className={cn(
        'flex shrink-0 items-center gap-2.5 border-b px-4 transition-colors hover:bg-muted',
        HEADER_HEIGHT,
      )}
    >
      {brandMark}
      {brandWords}
    </NavLink>
  ) : (
    <div className={cn('flex shrink-0 items-center gap-2.5 border-b px-4', HEADER_HEIGHT)}>
      {brandMark}
      {brandWords}
    </div>
  );

  /**
   * The account card: who you are, and the way out.
   *
   * The view switch sat here too and has moved to the top bar beside the
   * search — see the note there.
   */
  const userBlock = (
    <div className="shrink-0 border-t p-2.5">
      {/* h-11 is stated rather than inherited from whatever the name and role
          happen to measure, because the rail pins its avatar to the same
          number. Left implicit, a change to either font size would slide the
          two apart again and nothing would say why. */}
      <div className="flex h-11 items-center gap-1">
        {/* The whole card is the link — a name in the corner is where people
            look for their own account, so it has to be pressable, not decor. */}
        <NavLink
          to="/profile"
          onClick={() => setMobileOpen(false)}
          className={({ isActive }) =>
            cn(
              'flex min-w-0 flex-1 items-center gap-2.5 rounded-xl px-2 py-1.5 text-left transition-colors',
              isActive ? 'bg-muted' : 'hover:bg-muted',
            )
          }
        >
          <UserAvatar
            firstName={user.firstName}
            lastName={user.lastName}
            avatarUrl={user.avatarUrl}
            className="h-8 w-8 rounded-full"
            textClassName="text-[11px]"
          />
          <span className="min-w-0 flex-1 leading-tight">
            <span className="block truncate text-[13px] font-medium">{fullName(user)}</span>
            <span className="block truncate text-[11px] text-muted-foreground">
              {primaryRole}
              {user.departmentName ? ` · ${user.departmentName}` : ''}
            </span>
          </span>
        </NavLink>

        {/*
          Signing out, beside the name it signs out of.

          It was in the top bar among notifications and the calendar — a row of
          navigation with one irreversible action hidden in it, next to a bell
          people click without looking. Here it reads as what it is: the end of
          this person's session, attached to this person.
        */}
        <Button
          variant="ghost"
          size="icon"
          className="shrink-0 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          onClick={() => void logout()}
          title="Sign out"
          aria-label="Sign out"
        >
          <LogOut className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-background">
      {/*
        Desktop sidebar.

        Pinned to the viewport at exactly its height — `sticky top-0 h-screen`
        rather than a column that grows with the page. Without it, a long
        report or course list stretched the sidebar to the height of the
        document, so the brand block scrolled off the top and the user card sat
        a thousand pixels below the fold.
      */}
      <aside
        className={cn(
          'sticky top-0 hidden h-screen shrink-0 flex-col border-r bg-background transition-[width] duration-200 lg:flex',
          railed ? 'w-[68px]' : 'w-[236px]',
        )}
      >
        {railed ? (
          /* The mark stays; the wordmark is what needed the width. Still the
             way home on a course — just without the words. */
          <div className={cn('flex shrink-0 items-center justify-center border-b', HEADER_HEIGHT)}>
            {brandLinksHome ? (
              <NavLink
                to="/"
                title="Dashboard"
                aria-label="Go to the dashboard"
                className="flex h-8 w-8 items-center justify-center rounded-lg bg-white ring-1 ring-border transition-shadow hover:ring-2 hover:ring-brand-700"
              >
                <img src="/petpooja-mark.png" alt="Petpooja" className="h-5 w-5 object-contain" />
              </NavLink>
            ) : (
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white ring-1 ring-border">
                <img src="/petpooja-mark.png" alt="Petpooja" className="h-5 w-5 object-contain" />
              </span>
            )}
          </div>
        ) : (
          brandBlock
        )}

        {/* Collapsed on a course, and collapsed AND inert during an exam. */}
        {navList(railed, sittingAssessment)}

        {railed ? (
          /**
           * The avatar alone, and on the same line it already occupied.
           *
           * Sign out used to stack underneath it here. Stacked, this block
           * stood 96px against the expanded card's 64px, so collapsing the
           * panel lifted the avatar and hung an icon below where the sidebar
           * had previously ended — the rail disagreeing with itself about
           * where its own floor is.
           *
           * Nothing is stranded by dropping it: the avatar opens the profile,
           * and Sign out sits in that page's header. Two presses, along a path
           * people already take to reach their own account.
           *
           * Still nothing at all during an exam — signing out mid-paper leaves
           * a clock running on an attempt the candidate believes they left.
           */
          !sittingAssessment && (
            <div className="shrink-0 border-t p-2.5">
              {/* The same h-11 the expanded card states, which is what keeps
                  the avatar on one line across the collapse instead of
                  jumping. */}
              <div className="flex h-11 items-center justify-center">
                <NavLink
                  to="/profile"
                  title={fullName(user)}
                  aria-label={`Your profile — ${fullName(user)}`}
                  className={({ isActive }) =>
                    cn(
                      'rounded-full transition-colors',
                      // The ring is on the LINK, not the avatar: a picture
                      // fills its frame edge to edge, so an active state drawn
                      // as a background colour would be invisible behind it.
                      isActive && 'ring-2 ring-brand-800/30',
                    )
                  }
                >
                  <UserAvatar
                    firstName={user.firstName}
                    lastName={user.lastName}
                    avatarUrl={user.avatarUrl}
                    className="h-8 w-8 rounded-full"
                    textClassName="text-[11px]"
                  />
                </NavLink>
              </div>
            </div>
          )
        ) : (
          userBlock
        )}
      </aside>

      {/* Mobile drawer. Never opened during an attempt — the hamburger that
          opens it is hidden, and this guard makes that true rather than
          merely likely. */}
      {mobileOpen && !sittingAssessment && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
          />
          <aside className="relative flex h-full w-[280px] flex-col bg-background shadow-xl">
            <div className="flex items-center justify-between pr-3">
              {brandBlock}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileOpen(false)}
                aria-label="Close menu"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            {navList(false)}
            {userBlock}
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        {/*
          Opaque, not translucent-and-blurred.

          This was `bg-background/90 backdrop-blur`, which asks the compositor
          to re-blur the full width of whatever is behind it on every scroll
          frame — the single most expensive thing on the page, and it is
          pinned over everything that scrolls. Dropping it costs a translucency
          almost nobody could describe and buys back every frame it was eating.
        */}
        <header
          className={cn(
            'sticky top-0 z-30 flex items-center gap-3 border-b bg-background px-4 sm:px-6',
            HEADER_HEIGHT,
          )}
        >
          {!sittingAssessment && (
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </Button>
          )}

          {/* Where you are: the role you are acting as, then the page. */}
          <nav
            aria-label="Breadcrumb"
            className="hidden min-w-0 items-center gap-1.5 text-[13px] sm:flex"
          >
            <span className="text-muted-foreground">{primaryRole}</span>
            <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" aria-hidden />
            <span className="truncate font-medium">{currentPageTitle(location.pathname)}</span>
          </nav>

          <div className="flex flex-1 justify-end">
            {!sittingAssessment && (
            <button
              onClick={() => navigate('/catalogue')}
              className="hidden h-9 w-full max-w-[280px] items-center gap-2.5 rounded-xl border bg-card px-3 text-[13px] text-muted-foreground transition-colors hover:border-muted-foreground/30 md:flex"
            >
              <Search className="h-[15px] w-[15px] shrink-0" strokeWidth={1.75} />
              <span className="flex-1 text-left">Search everything</span>
              {/* The shortcut is real — see the listener above. A hint for a
                  key combination that does nothing is worse than no hint. */}
              <kbd className="shrink-0 rounded border bg-muted px-1.5 py-0.5 font-sans text-[10px] font-medium text-muted-foreground/80">
                {shortcutHint()}
              </kbd>
            </button>
            )}
          </div>

          {/*
            Everything that leaves the paper is gone until it is handed in.

            Signing out used to sit here and was included in that hiding for a
            reason: it is the one control that would otherwise end an attempt
            whose clock keeps running, leaving the sweeper to submit a
            half-finished paper the candidate thought they had escaped. It now
            lives on the user card, which is hidden during an attempt anyway —
            so that protection is unchanged.
          */}
          {!sittingAssessment && (
            <>
              {/*
                The view switch, first in the top-bar cluster.

                It sat on the user card, below the menu it changes — which put
                the cause underneath its own effect, at the very bottom of the
                sidebar. Up here it is beside the other controls that change
                what you are looking at rather than where you are, and the menu
                visibly redraws beneath it.

                Admins only, and never mid-paper: everything in this cluster is
                hidden during an attempt.
              */}
              {canSwitchView && (
                /* A Trainer's working view is not an admin view, and the
                   switch should not claim it is. */
                <ViewSwitch
                  role={roleSlugs.includes(SYSTEM_ROLES.ADMIN) ? 'admin' : 'trainer'}
                />
              )}

              {canCompose && (
                <Button
                  size="sm"
                  className="h-9 gap-1.5 px-2.5 sm:px-3"
                  onClick={() => navigate('/announcements?compose=1')}
                >
                  <Plus className="h-4 w-4" strokeWidth={2} />
                  {/* The label is the point of it; it folds away only where
                      the bar genuinely has no room. */}
                  <span className="hidden sm:inline">New announcement</span>
                  <span className="sr-only sm:hidden">New announcement</span>
                </Button>
              )}

              <NotificationBell />

              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9"
                onClick={() => navigate('/calendar')}
                aria-label="Calendar"
              >
                <Calendar className="h-[17px] w-[17px]" strokeWidth={1.75} />
              </Button>
            </>
          )}
        </header>

        {/* Inside the shell, so a page that fails to render keeps the nav and
            the user can leave it. Keyed on the path: navigating away clears
            the error, rather than leaving every link apparently dead. */}
        <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <ErrorBoundary resetKey={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>

      {/*
        In the shell rather than on each page, because every long screen wants
        it and the one that forgot would be the one that needed it. It is
        `fixed` and shows itself only once there is somewhere to go back to,
        so a short page carries no cost for having it here.
      */}
      <BackToTop />
    </div>
  );
}

/**
 * "⌘K" on a Mac, "Ctrl K" everywhere else.
 *
 * Read once at module load: the platform does not change mid-session, and
 * `navigator.platform` is deprecated but still the most reliable signal here.
 */
function shortcutHint(): string {
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
  return isMac ? '⌘K' : 'Ctrl K';
}

/** Header label for the current route. Permissions are irrelevant here. */
function currentPageTitle(pathname: string): string {
  const match = NAV_SECTIONS.flatMap((s) => s.items).find((item) => item.to === pathname);
  if (match) return match.label;
  // Nested routes — /admin/courses/:id — take the label of their section root.
  const parent = NAV_SECTIONS.flatMap((s) => s.items)
    .filter((item) => item.to !== '/' && pathname.startsWith(item.to))
    .sort((a, b) => b.to.length - a.to.length)[0];
  return parent?.label ?? '';
}
